import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './SearchResultsTab.css';

export interface SearchResultDocument {
  document_id: string;
  document_title?: string;
  relevance_score: number;
  kb_id?: string;
  kb_name?: string;
  justification?: string;
  relevance_explanation?: string;
  chunks?: Array<{
    chunk_id: string;
    text: string;
    chunk_score: number;
    page_number?: number;
    char_start?: number;
    char_end?: number;
  }>;
}

export interface SearchResultsData {
  tabId: string;
  tabName: string;
  documents: SearchResultDocument[];
  searchType: string;
  kbId: string;
  kbName?: string;
  destinationKbId?: string;
  destinationKbName?: string;
  query?: string;
  kwQuery?: string;
  smQuery?: string;
  searchMode?: string;
  topK?: number;
  topN?: number;
  enableReranking?: boolean;
  selectedFeatureIds?: string[];
  searchMetadata?: Record<string, unknown>;
  timestamp: string;
  isSaved: boolean;
  isSearchSaved?: boolean;
  searchRecordId?: string;
}

interface SearchResultsTabProps {
  data: SearchResultsData;
  selectedDocuments: Set<string>;
  onSelectionChange: (documentIds: string[]) => void;
  onSave: (tabId: string, selectedDocumentIds: string[]) => Promise<void>;
  onSaveSearch?: (tabId: string) => Promise<void>;
  onDocumentClick?: (documentId: string, document: SearchResultDocument) => void;
  selectedKbId?: string | null;
  selectedKbName?: string;
  destinationKbId?: string | null;
  destinationKbName?: string;
  subProjectId?: string;
}

const SearchResultsTab: React.FC<SearchResultsTabProps> = ({
  data,
  selectedDocuments,
  onSelectionChange,
  onSave,
  onSaveSearch,
  onDocumentClick,
  selectedKbId,
  selectedKbName,
  destinationKbId,
  destinationKbName,
  subProjectId,
}) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savingSearch, setSavingSearch] = useState(false);
  const [saveSearchError, setSaveSearchError] = useState<string | null>(null);

  const [expandedRE, setExpandedRE] = useState<Set<string>>(new Set());
  const [threshold, setThreshold] = useState<number>(0);
  const [maxDocs, setMaxDocs] = useState<number>(0);
  const [showQueries, setShowQueries] = useState(false);

  const toggleRE = (docId: string) => {
    setExpandedRE(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId); else next.add(docId);
      return next;
    });
  };

  const sortedDocuments = useMemo(() => {
    return [...data.documents].sort((a, b) => b.relevance_score - a.relevance_score);
  }, [data.documents]);

  const filteredDocuments = useMemo(() => {
    let docs = sortedDocuments;
    if (threshold > 0) {
      docs = docs.filter(d => d.relevance_score >= threshold / 100);
    }
    if (maxDocs > 0) {
      docs = docs.slice(0, maxDocs);
    }
    return docs;
  }, [sortedDocuments, threshold, maxDocs]);

  const effectiveKbId = destinationKbId || selectedKbId;
  const effectiveKbName = destinationKbId ? (destinationKbName || 'Destination KB') : selectedKbName;

  const applyFiltersToSelection = () => {
    const filteredIds = filteredDocuments.map(d => d.document_id);
    onSelectionChange(filteredIds);
  };

  useEffect(() => {
    // Clear error states when tab changes
    setSaveError(null);
    setSaveSearchError(null);
  }, [data.tabId, data.documents]);

  const toggleSelection = (documentId: string) => {
    const next = new Set(selectedDocuments);
    if (next.has(documentId)) {
      next.delete(documentId);
      console.log('[SearchResultsTab] ❌ Deselected document:', documentId);
    } else {
      next.add(documentId);
      console.log('[SearchResultsTab] ✅ Selected document:', documentId);
    }
    const newSelection = Array.from(next);
    console.log('[SearchResultsTab] 📤 Calling onSelectionChange:', {
      tabId: data.tabId,
      newSelection,
      selectionCount: newSelection.length
    });
    onSelectionChange(newSelection);
  };

  const handleSave = async () => {
    if (!effectiveKbId) {
      setSaveError(t('searchResults.selectKb', 'Select a knowledge base in the Metadata tab.'));
      return;
    }

    if (selectedDocuments.size === 0) {
      setSaveError(t('searchResults.selectDocument', 'Select at least one document.'));
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      await onSave(data.tabId, Array.from(selectedDocuments));
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSearch = async () => {
    if (!onSaveSearch || !subProjectId) {
      setSaveSearchError('Save search handler not provided');
      return;
    }

    setSavingSearch(true);
    setSaveSearchError(null);
    try {
      await onSaveSearch(data.tabId);
    } catch (err) {
      setSaveSearchError(err instanceof Error ? err.message : 'Failed to save search');
    } finally {
      setSavingSearch(false);
    }
  };

  const formatScore = (score: number): string => {
    return Math.round(score * 100) + '%';
  };

  const formatDate = (timestamp: string): string => {
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return timestamp;
    }
  };

  return (
    <div className="search-results-tab">
      <div className="srt-header">
        <div className="srt-header-left">
          <span className="srt-doc-count">
            {filteredDocuments.length}{filteredDocuments.length !== sortedDocuments.length ? `/${sortedDocuments.length}` : ''} {t('searchResults.documents', 'documents')}
          </span>
          <span className="srt-search-info">
            {data.searchType} | {data.kbName || data.kbId}
          </span>
          <span className="srt-timestamp">{formatDate(data.timestamp)}</span>
        </div>
        <div className="srt-header-right">
          {data.isSearchSaved && (
            <span className="srt-search-saved-badge">
              {t('searchResults.searchSaved', 'Search Saved')}
            </span>
          )}
          {!data.isSearchSaved && onSaveSearch && subProjectId && (
            <button
              className="btn btn-info btn-sm srt-save-search-btn"
              onClick={handleSaveSearch}
              disabled={savingSearch}
              title={t('searchResults.saveSearchTooltip', 'Save this search session to the database')}
            >
              {savingSearch
                ? t('searchResults.savingSearch', 'Saving Search...')
                : t('searchResults.saveSearch', 'Save Search')}
            </button>
          )}
          {data.isSaved ? (
            <span className="srt-saved-badge">
              {t('searchResults.saved', 'Saved')}
            </span>
          ) : (
            <button
              className="btn btn-primary btn-sm srt-save-btn"
              onClick={handleSave}
              disabled={saving || !effectiveKbId || selectedDocuments.size === 0}
            >
              {saving
                ? t('searchResults.saving', 'Saving...')
                : t(
                    'searchResults.saveSelectedTo',
                    'Save Selected Document(s) to {{kbName}}',
                    { kbName: effectiveKbName || t('searchResults.unknownKb', 'selected KB') }
                  )}
            </button>
          )}
          {saveSearchError && <span className="srt-error">{saveSearchError}</span>}
          {saveError && <span className="srt-error">{saveError}</span>}
        </div>
      </div>

      {/* Generated queries panel (collapsible) */}
      {(data.kwQuery || data.smQuery) && (
        <div className="srt-queries-panel">
          <button className="srt-queries-toggle" onClick={() => setShowQueries(!showQueries)}>
            {showQueries ? '▼' : '▶'} {t('searchResults.generatedQueries', 'Generated Queries')}
          </button>
          {showQueries && (
            <div className="srt-queries-content">
              {data.kwQuery && (
                <div className="srt-query-item">
                  <span className="srt-query-label">{t('searchResults.kwQuery', 'Keyword query:')}</span>
                  <code className="srt-query-text">{data.kwQuery}</code>
                </div>
              )}
              {data.smQuery && (
                <div className="srt-query-item">
                  <span className="srt-query-label">{t('searchResults.smQuery', 'Semantic query:')}</span>
                  <code className="srt-query-text">{data.smQuery}</code>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Filter toolbar */}
      <div className="srt-filter-bar">
        <label className="srt-filter-item">
          <span className="srt-filter-label">{t('searchResults.threshold', 'Min relevance %')}</span>
          <input
            type="number" min={0} max={100} step={5}
            value={threshold || ''}
            onChange={(e) => setThreshold(Number(e.target.value) || 0)}
            className="srt-filter-input"
            placeholder="0"
          />
        </label>
        <label className="srt-filter-item">
          <span className="srt-filter-label">{t('searchResults.maxDocs', 'Max docs')}</span>
          <input
            type="number" min={0} step={10}
            value={maxDocs || ''}
            onChange={(e) => setMaxDocs(Number(e.target.value) || 0)}
            className="srt-filter-input"
            placeholder={t('searchResults.all', 'All')}
          />
        </label>
        <button className="srt-filter-apply" onClick={applyFiltersToSelection}>
          {t('searchResults.selectFiltered', 'Select filtered')}
        </button>
      </div>

      <div className="srt-list">
        {filteredDocuments.map((doc, idx) => (
          <div key={doc.document_id} className="srt-document-item">
            <div
              className="srt-document-row"
              onClick={() => onDocumentClick?.(doc.document_id, doc)}
            >
              <div className="srt-checkbox-cell" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selectedDocuments.has(doc.document_id)}
                  onChange={() => toggleSelection(doc.document_id)}
                />
              </div>
              <span className="srt-rank">#{idx + 1}</span>
              <span className="srt-title">
                {doc.document_title || doc.document_id.substring(0, 12) + '...'}
              </span>
              <span className="srt-score">{formatScore(doc.relevance_score)}</span>
              {doc.justification && (
                <span className="srt-preview" title={doc.justification}>
                  {doc.justification.substring(0, 100)}{doc.justification.length > 100 ? '…' : ''}
                </span>
              )}
              {!doc.justification && doc.chunks && doc.chunks.length > 0 && (
                <span className="srt-preview" title={doc.chunks[0].text}>
                  {doc.chunks[0].text.substring(0, 100)}...
                </span>
              )}
              {doc.relevance_explanation && (
                <button
                  className="srt-re-toggle"
                  onClick={(e) => { e.stopPropagation(); toggleRE(doc.document_id); }}
                  title={t('searchResults.toggleRE', 'Toggle relevance explanation')}
                >
                  {expandedRE.has(doc.document_id) ? '▲' : '▼'}
                </button>
              )}
            </div>
            {doc.relevance_explanation && expandedRE.has(doc.document_id) && (
              <div className="srt-re-panel">
                {doc.relevance_explanation}
              </div>
            )}
          </div>
        ))}
        {filteredDocuments.length === 0 && (
          <div className="srt-empty">
            {t('searchResults.noResults', 'No documents found')}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResultsTab;
